import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Building2, Plus, Edit, Trash2, Check } from 'lucide-react';
import { businessService, type Business } from '@/services/supabase/business.service';
import { useAuth } from '@/context/AuthContext';
import { AddBusinessDialog } from './AddBusinessDialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export const BusinessCard: React.FC = () => {
  const { supabaseUser, firebaseUser } = useAuth();
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [selectedBusiness, setSelectedBusiness] = useState<Business | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingBusiness, setEditingBusiness] = useState<Business | null>(null);
  const [deletingBusinessId, setDeletingBusinessId] = useState<string | null>(null);
  const [isAssigning, setIsAssigning] = useState(false);

  const loadBusinesses = useCallback(async () => {
    if (!firebaseUser) return;

    setIsLoading(true);
    try {
      const data = await businessService.getUserBusinesses(firebaseUser.uid);
      setBusinesses(data);

      // Set the selected business based on user's business_id
      if (supabaseUser?.business_id) {
        const userBusiness = data.find((b) => b.id === supabaseUser.business_id);
        setSelectedBusiness(userBusiness || null);
      }
    } catch (error) {
      console.error('Error loading businesses:', error);
    } finally {
      setIsLoading(false);
    }
  }, [firebaseUser, supabaseUser]);

  useEffect(() => {
    loadBusinesses();
  }, [loadBusinesses]);

  const handleBusinessCreated = async (business: Business) => {
    await loadBusinesses();

    // Auto-assign the first business to the user
    if (businesses.length === 0 && firebaseUser) {
      await handleAssignBusiness(business);
    }
  };

  const handleAddBusiness = () => {
    setEditingBusiness(null);
    setIsDialogOpen(true);
  };

  const handleEditBusiness = (business: Business) => {
    setEditingBusiness(business);
    setIsDialogOpen(true);
  };

  const handleDeleteBusiness = async (businessId: string) => {
    if (!firebaseUser) return;

    try {
      await businessService.deleteBusiness(businessId);

      // If this was the assigned business, unassign it
      if (selectedBusiness?.id === businessId) {
        await businessService.assignBusinessToUser(firebaseUser.uid, null);
        setSelectedBusiness(null);
      }

      await loadBusinesses();
      setDeletingBusinessId(null);
    } catch (error) {
      console.error('Error deleting business:', error);
      alert('Failed to delete business. Please try again.');
    }
  };

  const handleAssignBusiness = async (business: Business) => {
    if (!firebaseUser) return;

    setIsAssigning(true);
    try {
      await businessService.assignBusinessToUser(firebaseUser.uid, business.id);
      setSelectedBusiness(business);

      // Refresh to update the user profile
      window.location.reload();
    } catch (error) {
      console.error('Error assigning business:', error);
      alert('Failed to assign business. Please try again.');
    } finally {
      setIsAssigning(false);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Business</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">Loading...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Business</span>
            <Button onClick={handleAddBusiness} variant="outline" size="sm" className="gap-2">
              <Plus className="h-4 w-4" />
              Add Business
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {businesses.length === 0 ? (
            <div className="text-center py-6">
              <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
              <Button onClick={handleAddBusiness} variant="outline" className="gap-2">
                <Plus className="h-4 w-4" />
                Add Your Business
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {businesses.map((business) => {
                const isSelected = selectedBusiness?.id === business.id;

                return (
                  <div
                    key={business.id}
                    className={`p-4 border rounded-lg transition-colors ${
                      isSelected ? 'border-primary bg-primary/5' : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3 flex-1">
                        {business.logo ? (
                          <img
                            src={business.logo}
                            alt={business.name}
                            className="h-10 w-10 rounded object-cover"
                          />
                        ) : (
                          <div className="h-10 w-10 rounded bg-primary/10 flex items-center justify-center">
                            <Building2 className="h-5 w-5 text-primary" />
                          </div>
                        )}

                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold">{business.name}</h3>
                            {isSelected && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-primary text-primary-foreground rounded">
                                <Check className="h-3 w-3" />
                                Active
                              </span>
                            )}
                          </div>
                          {business.tax_id && (
                            <p className="text-sm text-muted-foreground mt-1">
                              Tax ID: {business.tax_id}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        {!isSelected && (
                          <Button
                            onClick={() => handleAssignBusiness(business)}
                            variant="ghost"
                            size="sm"
                            disabled={isAssigning}
                          >
                            Use
                          </Button>
                        )}
                        <Button
                          onClick={() => handleEditBusiness(business)}
                          variant="ghost"
                          size="sm"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          onClick={() => setDeletingBusinessId(business.id)}
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Business Dialog */}
      <AddBusinessDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        onBusinessCreated={handleBusinessCreated}
        existingBusiness={editingBusiness}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={deletingBusinessId !== null}
        onOpenChange={(open) => !open && setDeletingBusinessId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Business?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this business? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deletingBusinessId && handleDeleteBusiness(deletingBusinessId)}
              className="bg-red-500 hover:bg-red-600"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
